import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { isPayByUser } from '../../shared/constants';
import { ProductApiModel } from '../../shared/models/ProductModel';
import { accountSelector } from '../../shared/store/shared/accountSlice';
import { useGetDealProductsQuery } from './apiSlice';

export interface UseDealTotalSpendResult {
  /** Whether the deal uses the Pay-by-User payment arrangement. */
  isPayByUserDeal: boolean;
  /**
   * True only while the initial spend/savings request is in flight for a
   * Pay-by-User deal, i.e. when no value can be shown yet. Background refetches
   * keep the previously resolved totals visible instead of flashing a
   * placeholder, and non-Pay-by-User deals are never pending because their
   * totals arrive with the deal itself.
   */
  isPending: boolean;
  /** Resolve the Total Spent for a single product. */
  getProductSpend: (product: ProductApiModel) => number;
  /** Resolve the Total Savings for a single product. */
  getProductSavings: (product: ProductApiModel) => number;
  /** Total Spent across the whole deal. */
  totalSpend: number;
  /** Total Savings across the whole deal. */
  totalSavings: number;
}

const sumValues = (map: Map<string, number>) => {
  let total = 0;
  map.forEach((val) => {
    total += val;
  });
  return total;
};

/**
 * Resolves "Total Spent" and "Total Savings" values for a deal.
 *
 * Pay-by-User deals fetch spend/savings from the deal products endpoint (keyed by SKU),
 * while other arrangements reuse the values already present on the deal/products.
 */
export default function useDealTotalSpend(
  dealId: string,
  paymentArrangement: string,
  fallbackTotalSpend = 0,
  fallbackTotalSavings = 0,
): UseDealTotalSpendResult {
  const { currentAccountId } = useSelector(accountSelector);
  const isPbu = isPayByUser(paymentArrangement);

  // Only Pay-by-User deals need the per-deal spend endpoint call. isLoading (as
  // opposed to isFetching) is true only for the first request of a cache key, so
  // cached values survive refetches without reverting to a placeholder.
  const { data, isLoading } = useGetDealProductsQuery(
    { accountId: currentAccountId, dealId },
    { skip: !isPbu || !currentAccountId },
  );

  const totalsBySku = useMemo(() => {
    const spend = new Map<string, number>();
    const savings = new Map<string, number>();
    data?.products?.forEach((p: ProductApiModel) => {
      spend.set(p.sku, p.totalSpend ?? 0);
      savings.set(p.sku, p.totalSavings ?? 0);
    });
    return { spend, savings };
  }, [data]);

  const getProductSpend = useCallback(
    (product: ProductApiModel) =>
      isPbu ? totalsBySku.spend.get(product.sku) ?? 0 : product.totalSpend ?? 0,
    [isPbu, totalsBySku],
  );

  const getProductSavings = useCallback(
    (product: ProductApiModel) =>
      isPbu
        ? totalsBySku.savings.get(product.sku) ?? 0
        : product.totalSavings ?? 0,
    [isPbu, totalsBySku],
  );

  const totalSpend = useMemo(
    () => (isPbu ? sumValues(totalsBySku.spend) : fallbackTotalSpend),
    [isPbu, totalsBySku, fallbackTotalSpend],
  );

  const totalSavings = useMemo(
    () => (isPbu ? sumValues(totalsBySku.savings) : fallbackTotalSavings),
    [isPbu, totalsBySku, fallbackTotalSavings],
  );

  return {
    isPayByUserDeal: isPbu,
    isPending: isPbu && isLoading,
    getProductSpend,
    getProductSavings,
    totalSpend,
    totalSavings,
  };
}
